import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient } from '../../../lib/connect/clients';
import { Choice, TextField } from '../PastPerformance/Fields';
import { observationVersion, periodMonthsError, scenarioMoney } from './blackHatScenarioDrafts';
const units = [
    {
        value: '1',
        label: 'HOUR'
    },
    {
        value: '2',
        label: 'DAY'
    },
    {
        value: '3',
        label: 'ITEM'
    }
];
const unitLabel = (unit)=>units.find((u)=>u.value === String(unit))?.label || 'Unknown unit';
const roles = [
    {
        value: '0',
        label: 'Unassigned'
    },
    {
        value: '1',
        label: 'Low'
    },
    {
        value: '2',
        label: 'Likely'
    },
    {
        value: '3',
        label: 'High'
    }
];
const roleLabel = (role)=>roles.find((r)=>r.value === String(role))?.label ?? 'Unknown';
const newLine = ()=>({
        id: crypto.randomUUID(),
        label: '',
        quantity: '100',
        unit: 1
    });
export function BlackHatScenarioFacts({ scenarios }) {
    if (!scenarios.length) return /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        children: "No pricing scenarios · Pricing: UNKNOWN"
    });
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-3",
        children: scenarios.map((s)=>/*#__PURE__*/ _jsxs("section", {
                "data-testid": Testids.blackHat.scenarioResult,
                className: "space-y-2 rounded-md border p-3",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            s.label,
                            " · ",
                            s.pricingState,
                            " · ",
                            s.currency
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            "Role: ",
                            s.role ? roleLabel(s.role).toLowerCase() : 'unassigned',
                            " · Period:",
                            ' ',
                            s.periodMonths ? `${s.periodMonths} months` : 'not set'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        "data-testid": Testids.blackHat.scenarioSubtotal,
                        children: [
                            "Known subtotal: ",
                            scenarioMoney(s.knownSubtotalCents)
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        "data-testid": Testids.blackHat.scenarioTotal,
                        children: [
                            "Effective total:",
                            ' ',
                            s.effectiveTotalCents === undefined ? 'Unknown (PARTIAL)' : scenarioMoney(s.effectiveTotalCents)
                        ]
                    }),
                    !!s.unpricedLineIds.length && /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        "data-testid": Testids.blackHat.scenarioUnpriced,
                        children: [
                            "Unpriced lines:",
                            ' ',
                            s.unpricedLineIds.map((id)=>s.lines.find((l)=>l.id === id)?.label || id).join(', '),
                            ". Excluded from known subtotal."
                        ]
                    }),
                    s.override && /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        "data-testid": Testids.blackHat.scenarioOverride,
                        children: [
                            "Explicit override: ",
                            scenarioMoney(s.override.valueCents),
                            " · Reason:",
                            ' ',
                            s.override.reason,
                            " · Actor: ",
                            s.override.actorUserId,
                            ". Computed basis retained above."
                        ]
                    }),
                    s.lines.map((l)=>/*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1 [overflow-wrap:anywhere]",
                            "data-testid": Testids.blackHat.scenarioSavedLine,
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        l.label,
                                        ": ",
                                        l.quantity,
                                        " ",
                                        unitLabel(l.unit),
                                        " ·",
                                        ' ',
                                        l.amountCents === undefined ? 'Unpriced' : scenarioMoney(l.amountCents)
                                    ]
                                }),
                                l.rate && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Rate: ",
                                                l.rate.amount,
                                                " USD/",
                                                unitLabel(l.rate.unit),
                                                " · ",
                                                l.rate.source,
                                                ' ',
                                                l.rate.source === 'observation' && `${l.rate.observationId} · version ${l.rate.observationVersion}`,
                                                ' ',
                                                "· Selected by: ",
                                                l.rate.actorUserId
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            className: "whitespace-pre-wrap [overflow-wrap:anywhere]",
                                            children: [
                                                "Source: ",
                                                l.rate.provenance
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }, l.id)),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            "Calculation: ",
                            s.calculationVersion
                        ]
                    })
                ]
            }, s.id))
    });
}
export function BlackHatScenarios({ scenarios, onChange }) {
    const { user } = useAuth();
    const tenant = user?.tenant_id ?? '';
    const catalog = useQuery({
        queryKey: [
            'black-hat-rate-catalog',
            tenant
        ],
        enabled: !!tenant && scenarios.length > 0,
        retry: false,
        queryFn: async ()=>{
            const rows = [];
            for(let offset = 0;; offset += 200){
                const page = await deliveryClient.listRateObservations({
                    companyId: tenant,
                    includeCorrected: true,
                    offset,
                    limit: 200
                });
                rows.push(...page.observations);
                if (!page.observations.length || BigInt(rows.length) >= page.total) return rows;
            }
        }
    });
    const rows = catalog.data ?? [];
    const eligible = rows.filter((r)=>r.currency === 'USD' && r.rateUnit === 'HOURLY' && r.observationType !== 'PAY' && !r.invalidatedAt && observationVersion(r.id, rows));
    function update(s) {
        onChange(scenarios.map((old)=>old.id === s.id ? s : old));
    }
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.blackHat.scenarios,
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: "Optional early pricing scenarios. Leave empty for qualitative Black Hat. Quantities and USD rates accept plain decimals with up to four fractional digits. HOUR, DAY and ITEM must match exactly; no conversion. Save to compute totals."
            }),
            scenarios.map((s)=>/*#__PURE__*/ _jsxs("section", {
                    "aria-label": s.label || 'New pricing scenario',
                    "data-scenario-id": s.id,
                    "data-testid": Testids.blackHat.scenario,
                    className: "space-y-3 rounded-md border p-3",
                    children: [
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "Scenario name",
                            testId: Testids.blackHat.scenarioLabel,
                            value: s.label,
                            onChange: (label)=>update({
                                    ...s,
                                    label
                                })
                        }),
                        /*#__PURE__*/ _jsx(Choice, {
                            label: "Scenario role",
                            testId: Testids.blackHat.scenarioRoleSelect,
                            value: String(s.role),
                            options: roles.map((r)=>{
                                const holder = scenarios.find((o)=>o.id !== s.id && String(o.role) === r.value);
                                return r.value !== '0' && holder ? {
                                    value: r.value,
                                    label: `${r.label} — already used by ${holder.label || 'another scenario'}`,
                                    disabled: true
                                } : r;
                            }),
                            onChange: (role)=>update({
                                    ...s,
                                    role: Number(role)
                                })
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "Period (months)",
                            testId: Testids.blackHat.scenarioPeriodInput,
                            value: s.periodMonths,
                            error: periodMonthsError(s.periodMonths),
                            onChange: (periodMonths)=>update({
                                    ...s,
                                    periodMonths
                                })
                        }),
                        s.lines.map((l)=>{
                            const setLine = (line)=>update({
                                    ...s,
                                    lines: s.lines.map((old)=>old.id === line.id ? line : old)
                                });
                            const rate = l.rate;
                            return /*#__PURE__*/ _jsxs("section", {
                                "aria-label": l.label || 'New scenario line',
                                "data-line-id": l.id,
                                "data-testid": Testids.blackHat.scenarioLine,
                                className: "space-y-2 rounded-md border p-3",
                                children: [
                                    /*#__PURE__*/ _jsx(TextField, {
                                        label: "Line label",
                                        testId: Testids.blackHat.scenarioLineLabel,
                                        value: l.label,
                                        onChange: (label)=>setLine({
                                                ...l,
                                                label
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx(TextField, {
                                        label: "Quantity",
                                        testId: Testids.blackHat.scenarioQuantity,
                                        value: l.quantity,
                                        onChange: (quantity)=>setLine({
                                                ...l,
                                                quantity
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx(Choice, {
                                        label: "Quantity unit",
                                        testId: Testids.blackHat.scenarioUnit,
                                        value: String(l.unit),
                                        options: units,
                                        onChange: (unit)=>setLine({
                                                ...l,
                                                unit: Number(unit)
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx(Choice, {
                                        label: "Rate source",
                                        testId: Testids.blackHat.scenarioRateSource,
                                        value: rate?.source ?? 'unpriced',
                                        options: [
                                            {
                                                value: 'unpriced',
                                                label: 'Unpriced — unknown, excluded from subtotal'
                                            },
                                            {
                                                value: 'manual',
                                                label: 'Manual USD rate with source'
                                            },
                                            {
                                                value: 'observation',
                                                label: 'Pinned Rate Book observation version'
                                            }
                                        ],
                                        onChange: (source)=>setLine({
                                                ...l,
                                                rate: source === 'unpriced' ? undefined : {
                                                    source,
                                                    observationId: '',
                                                    observationVersion: 0,
                                                    amount: '',
                                                    unit: source === 'manual' ? l.unit : 0,
                                                    provenance: ''
                                                }
                                            })
                                    }),
                                    !rate && /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: "Unpriced · this line makes the scenario PARTIAL."
                                    }),
                                    rate?.source === 'manual' && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(TextField, {
                                                label: "Manual USD per unit",
                                                testId: Testids.blackHat.scenarioRateAmount,
                                                value: rate.amount,
                                                onChange: (amount)=>setLine({
                                                        ...l,
                                                        rate: {
                                                            ...rate,
                                                            amount
                                                        }
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(Choice, {
                                                label: "Rate unit",
                                                testId: Testids.blackHat.scenarioRateUnit,
                                                value: String(rate.unit),
                                                options: units,
                                                onChange: (unit)=>setLine({
                                                        ...l,
                                                        rate: {
                                                            ...rate,
                                                            unit: Number(unit)
                                                        }
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(TextField, {
                                                label: "Rate source",
                                                testId: Testids.blackHat.scenarioProvenance,
                                                value: rate.provenance,
                                                onChange: (provenance)=>setLine({
                                                        ...l,
                                                        rate: {
                                                            ...rate,
                                                            provenance
                                                        }
                                                    })
                                            })
                                        ]
                                    }),
                                    rate?.source === 'observation' && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                                "aria-label": "Rate observation version",
                                                "data-testid": Testids.blackHat.scenarioRateVersion,
                                                value: rate.observationId,
                                                onChange: (e)=>{
                                                    const id = e.target.value;
                                                    const version = observationVersion(id, rows);
                                                    if (version) setLine({
                                                        ...l,
                                                        rate: {
                                                            ...rate,
                                                            observationId: id,
                                                            observationVersion: version
                                                        }
                                                    });
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "",
                                                        disabled: true,
                                                        children: "Choose an immutable observation version"
                                                    }),
                                                    rate.observationId && !eligible.some((r)=>r.id === rate.observationId) && /*#__PURE__*/ _jsxs("option", {
                                                        value: rate.observationId,
                                                        children: [
                                                            "Retained pin · ",
                                                            rate.observationId,
                                                            " · version ",
                                                            rate.observationVersion
                                                        ]
                                                    }),
                                                    eligible.map((r)=>/*#__PURE__*/ _jsxs("option", {
                                                            value: r.id,
                                                            children: [
                                                                r.lcatTitle || r.id,
                                                                " · ",
                                                                r.observationType,
                                                                " · version",
                                                                ' ',
                                                                observationVersion(r.id, rows),
                                                                " · ",
                                                                r.companyName,
                                                                " · ",
                                                                r.vehicleName,
                                                                " ·",
                                                                ' ',
                                                                r.periodLabel,
                                                                " · ",
                                                                r.worksite,
                                                                " · ",
                                                                r.locality,
                                                                " · ",
                                                                r.burdenBasis,
                                                                " ·",
                                                                ' ',
                                                                r.source,
                                                                " · ",
                                                                r.id
                                                            ]
                                                        }, r.id))
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(SpecraText, {
                                                as: "p",
                                                children: [
                                                    "Pinned: ",
                                                    rate.observationId || 'Choose a rate',
                                                    " · version",
                                                    ' ',
                                                    rate.observationVersion || 'not selected',
                                                    ". Corrections never replace this selection automatically. Exact USD/hour amount and source are filled in on save."
                                                ]
                                            }),
                                            catalog.isFetching && /*#__PURE__*/ _jsx("output", {
                                                children: "Loading rate versions…"
                                            }),
                                            catalog.isError && /*#__PURE__*/ _jsxs("div", {
                                                role: "alert",
                                                children: [
                                                    "Rate catalog unavailable; existing pins are retained.",
                                                    ' ',
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        onClick: ()=>void catalog.refetch(),
                                                        children: "Retry rate catalog"
                                                    })
                                                ]
                                            }),
                                            !catalog.isFetching && !catalog.isError && !eligible.length && /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                children: "No compatible rate versions available. Use a manual rate or leave unpriced."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        "data-testid": Testids.blackHat.scenarioRemoveLine,
                                        onClick: ()=>update({
                                                ...s,
                                                lines: s.lines.filter((old)=>old.id !== l.id)
                                            }),
                                        children: "Remove line"
                                    })
                                ]
                            }, l.id);
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": Testids.blackHat.scenarioAddLine,
                            disabled: s.lines.length >= 200,
                            onClick: ()=>update({
                                    ...s,
                                    lines: [
                                        ...s.lines,
                                        newLine()
                                    ]
                                }),
                            children: "Add scenario line"
                        }),
                        s.override ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Override total (integer USD cents)",
                                    testId: Testids.blackHat.scenarioOverrideCents,
                                    value: s.override.valueCents,
                                    onChange: (valueCents)=>update({
                                            ...s,
                                            override: {
                                                valueCents,
                                                reason: s.override?.reason ?? ''
                                            }
                                        })
                                }),
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Override reason",
                                    testId: Testids.blackHat.scenarioOverrideReason,
                                    value: s.override.reason,
                                    onChange: (reason)=>update({
                                            ...s,
                                            override: {
                                                valueCents: s.override?.valueCents ?? '',
                                                reason
                                            }
                                        })
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    "data-testid": Testids.blackHat.scenarioRemoveOverride,
                                    onClick: ()=>update({
                                            ...s,
                                            override: undefined
                                        }),
                                    children: "Remove total override"
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": Testids.blackHat.scenarioAddOverride,
                            onClick: ()=>update({
                                    ...s,
                                    override: {
                                        valueCents: '',
                                        reason: ''
                                    }
                                }),
                            children: "Add explicit total override"
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            children: "An override retains the computed subtotal and unpriced lines. The server records who chose it."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "ghost",
                            "data-testid": Testids.blackHat.scenarioRemove,
                            onClick: ()=>onChange(scenarios.filter((old)=>old.id !== s.id)),
                            children: "Remove scenario"
                        })
                    ]
                }, s.id)),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": Testids.blackHat.scenarioAdd,
                disabled: scenarios.length >= 20,
                onClick: ()=>onChange([
                        ...scenarios,
                        {
                            id: crypto.randomUUID(),
                            label: '',
                            role: 0,
                            periodMonths: '',
                            lines: [
                                newLine()
                            ]
                        }
                    ]),
                children: "Add optional pricing scenario"
            })
        ]
    });
}
